import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/InterviewResults.css";
import logo from "../assets/logo1.png";
import 'bootstrap-icons/font/bootstrap-icons.css';
import jsPDF from 'jspdf';

const InterviewResults = () => {
  const [interviewData, setInterviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState('overview');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInterviewData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }

        console.log("Fetching interview results...");
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/users/interview-responses`,
          {
            headers: { Authorization: token },
          }
        );

        console.log("Interview data received:", response.data);
        
        if (!response.data || !response.data.analysis) {
          throw new Error("No analysis data received");
        }

        setInterviewData(response.data);
      } catch (err) {
        console.error("Error fetching interview data:", err);
        setError(err.response?.data?.message || "Error loading interview results.");
      } finally {
        setLoading(false);
      }
    };

    fetchInterviewData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handleDownload = () => {
    if (!interviewData) return;

    // Create new PDF document
    const doc = new jsPDF();
    let yPos = 20;
    
    // Add title
    doc.setFontSize(16);
    doc.text('INTERVIEW RESULTS', 20, yPos);
    yPos += 15;

    // Add total score
    doc.setFontSize(14);
    doc.text(`TOTAL SCORE: ${interviewData.score}/100`, 20, yPos);
    yPos += 20;

    // Add analysis section
    doc.setFontSize(14);
    doc.text('DETAILED ANALYSIS', 20, yPos);
    yPos += 10;

    // Add each analysis item
    interviewData.analysis.forEach((item, index) => {
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(12);
      doc.text(`Criterion ${index + 1}`, 20, yPos);
      yPos += 7;
      doc.text(`Puntaje: ${item.score}`, 20, yPos);
      yPos += 7;

      // Split long feedback text into multiple lines
      const splitFeedback = doc.splitTextToSize(item.feedback, 170);
      splitFeedback.forEach(line => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 7;
      });
      yPos += 10;
    });

    // Save the PDF
    doc.save(`Interview_Results_${new Date().toLocaleDateString()}.pdf`);
  };

  const handleDownloadAnalysis = () => {
    if (!interviewData) return;

    const doc = new jsPDF();
    let yPos = 20;
    
    // Title and score
    doc.setFontSize(16);
    doc.text('COMPLETE INTERVIEW ANALYSIS', 20, yPos);
    yPos += 15;
    
    doc.setFontSize(14);
    doc.text(`Total Score: ${interviewData.score}/100`, 20, yPos);
    yPos += 20;

    // For each question
    interviewData.questions.forEach((question, index) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      // Question number and score
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(`Question ${index + 1} (Score: ${interviewData.analysis[index].score}/100)`, 20, yPos);
      yPos += 10;

      // Question
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 150);
      doc.text('Question:', 20, yPos);
      yPos += 7;
      const splitQuestion = doc.splitTextToSize(question, 170);
      splitQuestion.forEach(line => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 7;
      });

      // Your answer
      yPos += 5;
      doc.setTextColor(0, 100, 0);
      doc.text('Your Answer:', 20, yPos);
      yPos += 7;
      doc.setTextColor(0, 0, 0);
      const splitAnswer = doc.splitTextToSize(interviewData.responses[index], 170);
      splitAnswer.forEach(line => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 7;
      });

      // Feedback
      yPos += 5;
      doc.setTextColor(150, 0, 0);
      doc.text('Feedback:', 20, yPos);
      yPos += 7;
      doc.setTextColor(0, 0, 0);
      const splitFeedback = doc.splitTextToSize(interviewData.analysis[index].feedback, 170);
      splitFeedback.forEach(line => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 7;
      });

      // Suggested Answer
      yPos += 5;
      doc.setTextColor(0, 100, 0);
      doc.text('Suggested Answer:', 20, yPos);
      yPos += 7;
      doc.setTextColor(0, 0, 0);
      const splitSuggested = doc.splitTextToSize(interviewData.analysis[index].suggestedAnswer, 170);
      splitSuggested.forEach(line => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 7;
      });

      yPos += 15;
    });

    doc.save(`Complete_Interview_Analysis_${new Date().toLocaleDateString()}.pdf`);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading results...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-container">
          <i className="bi bi-exclamation-circle"></i>
          <p>{error}</p>
        </div>
      );
    }

    if (!interviewData) return null;

    switch (activeSection) {
      case 'questions':
        return (
          <div className="results-section">
            <h3>
              <i className="bi bi-chat-dots"></i>
              Questions and Answers
            </h3>
            <div className="qa-list">
              {interviewData.questions.map((question, index) => (
                <div key={index} className="qa-card">
                  <div className="question">
                    <span className="q-number">Q{index + 1}</span>
                    <p>{question}</p>
                  </div>
                  <div className="answer">
                    <h5>Your Answer:</h5>
                    <p>{interviewData.responses[index]}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'analysis':
        return (
          <div className="results-section">
            <h3>
              <i className="bi bi-graph-up"></i>
              Detailed Analysis
            </h3>
            <div className="analysis-grid">
              {interviewData.analysis.map((item, index) => (
                <div key={index} className="analysis-card">
                  <div className="analysis-header">
                    <div className="score-badge" style={{ 
                      backgroundColor: `hsl(${item.score * 1.2}, 70%, 50%)`
                    }}>
                      {item.score}
                    </div>
                    <h4>Question {index + 1}</h4>
                  </div>
                  <div className="question-container">
                    <h5 className="section-title">
                      <i className="bi bi-question-circle"></i> Question
                    </h5>
                    <p>{interviewData.questions[index]}</p>
                  </div>
                  <div className="answer-container">
                    <h5 className="section-title">
                      <i className="bi bi-chat-left-text"></i> Your Answer
                    </h5>
                    <p>{interviewData.responses[index]}</p>
                  </div>
                  <div className="feedback-container">
                    <h5 className="section-title">
                      <i className="bi bi-lightbulb"></i> Feedback
                    </h5>
                    <p>{item.feedback}</p>
                  </div>
                  <div className="suggested-container">
                    <h5 className="section-title">
                      <i className="bi bi-star"></i> Suggested Answer
                    </h5>
                    <p>{item.suggestedAnswer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Navbar */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-gray py-3 fixed-top">
        <div className="container-fluid">
          <div className="d-flex align-items-center" style={{ maxWidth: '80%' }}>
            
            <Link className="navbar-brand h1 text_format" to="/dashboard" style={{ color: "#fff" }}>
              MIRAI Academic
            </Link>
          </div>
          
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto">
              <li className="nav-item">
                <Link className="nav-link" to="/dashboard">
                  <i className="bi bi-house-door"></i> Dashboard
                </Link>
              </li>
            
              
              <li className="nav-item">
                <button onClick={handleLogout} className="nav-link btn btn-link">
                  <i className="bi bi-box-arrow-right"></i> Logout
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Main Layout simplificado */}
      <div className="dashboard-layout">
        <div className="dashboard-main">
          {loading ? (
            <div className="loading-container">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Loading results...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <i className="bi bi-exclamation-circle"></i>
              <p>{error}</p>
            </div>
          ) : (
            <div className="results-section">
              <div className="results-header">
                <h3>
                  <i className="bi bi-graph-up"></i>
                  Interview Analysis
                </h3>
                <div className="results-actions">
                  <button className="btn btn-primary" onClick={handleDownloadAnalysis}>
                    <i className="bi bi-download"></i> Download Complete Report
                  </button>
                  <button className="btn btn-outline-primary" onClick={() => navigate("/dashboard")}>
                    <i className="bi bi-arrow-left"></i> Return to Dashboard
                  </button>
                </div>
              </div>

              <div className="analysis-grid">
                {interviewData?.analysis.map((item, index) => (
                  <div key={index} className="analysis-card">
                    <div className="analysis-header">
                      <div className="score-badge" style={{ 
                        backgroundColor: `hsl(${item.score * 1.2}, 70%, 50%)`
                      }}>
                        {item.score}
                      </div>
                      <h4>Question {index + 1}</h4>
                    </div>
                    <div className="question-container">
                      <h5 className="section-title">
                        <i className="bi bi-question-circle"></i> Question
                      </h5>
                      <p>{interviewData.questions[index]}</p>
                    </div>
                    <div className="answer-container">
                      <h5 className="section-title">
                        <i className="bi bi-chat-left-text"></i> Your Answer
                      </h5>
                      <p>{interviewData.responses[index]}</p>
                    </div>
                    <div className="feedback-container">
                      <h5 className="section-title">
                        <i className="bi bi-lightbulb"></i> Feedback
                      </h5>
                      <p>{item.feedback}</p>
                    </div>
                    <div className="suggested-container">
                      <h5 className="section-title">
                        <i className="bi bi-star"></i> Suggested Answer
                      </h5>
                      <p>{item.suggestedAnswer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default InterviewResults;
